import React from "react";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";

import "./FeaturedProducts.css";

/* ============================================================
   API
============================================================ */

const API =
  process.env.NODE_ENV === "production"
    ? "https://gj-frio-industrial-api.onrender.com"
    : "http://localhost:4000";

/* ============================================================
   OBTENER PRODUCTOS DESTACADOS
============================================================ */

const obtenerProductosDestacados = async () => {
  const respuesta = await axios.get(`${API}/api/featured-products`, {
    timeout: 45000,
  });

  return Array.isArray(respuesta.data) ? respuesta.data : [];
};

/* ============================================================
   COMPONENTE
============================================================ */

const FeaturedProducts = () => {
  /* ==========================================================
     TANSTACK QUERY
  ========================================================== */

  const {
    data: productos = [],
    isError,
    error,
  } = useQuery({
    queryKey: ["featured-products"],
    queryFn: obtenerProductosDestacados,

    // También están definidos globalmente en queryClient.js.
    staleTime: Infinity,
    gcTime: Infinity,
  });

  /* ==========================================================
     ERROR
  ========================================================== */

  if (isError) {
    console.error("Error cargando productos destacados:", error);
  }

  /* ==========================================================
     SEPARAR LOS DOS GRUPOS
  ========================================================== */

  const grupo1 = productos
    .filter((producto) => Number(producto.grupo) === 1)
    .sort((a, b) => Number(a.orden || 0) - Number(b.orden || 0));

  const grupo2 = productos
    .filter((producto) => Number(producto.grupo) === 2)
    .sort((a, b) => Number(a.orden || 0) - Number(b.orden || 0));

  /* ==========================================================
     DIVIDIR PRODUCTOS EN GRUPOS
  ========================================================== */

  const dividirEnGrupos = (listaProductos, cantidad) => {
    const grupos = [];

    for (let i = 0; i < listaProductos.length; i += cantidad) {
      grupos.push(listaProductos.slice(i, i + cantidad));
    }

    return grupos;
  };

  /* ==========================================================
     RENDERIZAR CARRUSEL
  ========================================================== */

  const renderCarousel = (listaProductos, carouselId, titulo, subtitulo) => {
    if (!listaProductos.length) {
      return null;
    }

    const slides = dividirEnGrupos(listaProductos, 3);

    return (
      <section className="featured-section">
        {/* ENCABEZADO */}

        <div className="featured-header">
          <div>
            <span className="featured-kicker">G&J FRÍO</span>

            <h2>{titulo}</h2>

            <p>{subtitulo}</p>
          </div>

          <div className="featured-header-line"></div>
        </div>

        {/* CARRUSEL */}

        <div
          id={carouselId}
          className="carousel slide featured-carousel"
          data-bs-ride={slides.length > 1 ? "carousel" : undefined}
          data-bs-interval="6000"
        >
          <div className="carousel-inner">
            {slides.map((slide, slideIndex) => (
              <div
                className={`carousel-item ${slideIndex === 0 ? "active" : ""}`}
                key={`${carouselId}-${slideIndex}`}
              >
                <div className="featured-grid">
                  {slide.map((producto, index) => (
                    <article
                      className="featured-card"
                      key={
                        producto._id || `${carouselId}-${slideIndex}-${index}`
                      }
                    >
                      {/* =====================================
                          IMAGEN
                      ===================================== */}

                      <div className="featured-image-wrapper">
                        <img
                          src={producto.imagen}
                          alt={producto.nombre}
                          className="featured-image"
                          loading="lazy"
                          decoding="async"
                          fetchpriority="low"
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />

                        {producto.categoria && (
                          <span className="featured-category">
                            {producto.categoria}
                          </span>
                        )}
                      </div>

                      {/* INFORMACIÓN */}

                      <div className="featured-card-body">
                        <h3>{producto.nombre}</h3>

                        {producto.descripcion && <p>{producto.descripcion}</p>}

                        <div className="featured-card-footer">
                          <span>
                            <i className="bi bi-check-circle-fill"></i>
                            Producto disponible
                          </span>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* FLECHAS */}

          {slides.length > 1 && (
            <>
              <button
                className="carousel-control-prev featured-control featured-control-prev"
                type="button"
                data-bs-target={`#${carouselId}`}
                data-bs-slide="prev"
              >
                <span className="featured-arrow">
                  <i className="bi bi-chevron-left"></i>
                </span>

                <span className="visually-hidden">Anterior</span>
              </button>

              <button
                className="carousel-control-next featured-control featured-control-next"
                type="button"
                data-bs-target={`#${carouselId}`}
                data-bs-slide="next"
              >
                <span className="featured-arrow">
                  <i className="bi bi-chevron-right"></i>
                </span>

                <span className="visually-hidden">Siguiente</span>
              </button>
            </>
          )}

          {/* INDICADORES */}

          {slides.length > 1 && (
            <div className="carousel-indicators featured-indicators">
              {slides.map((_, index) => (
                <button
                  key={index}
                  type="button"
                  data-bs-target={`#${carouselId}`}
                  data-bs-slide-to={index}
                  className={index === 0 ? "active" : ""}
                  aria-current={index === 0 ? "true" : undefined}
                  aria-label={`Slide ${index + 1}`}
                ></button>
              ))}
            </div>
          )}
        </div>
      </section>
    );
  };

  /* ==========================================================
     SIN PRODUCTOS
  ========================================================== */

  if (grupo1.length === 0 && grupo2.length === 0) {
    return null;
  }

  /* ==========================================================
     CONTENIDO
  ========================================================== */

  return (
    <main className="featured-products-page">
      {renderCarousel(
        grupo1,
        "featuredCarousel1",
        "Productos destacados",
        "Una selección de productos para refrigeración, lavadoras y uso técnico.",
      )}

      {renderCarousel(
        grupo2,
        "featuredCarousel2",
        "Más productos",
        "Repuestos y componentes seleccionados para trabajos profesionales.",
      )}
    </main>
  );
};

export default FeaturedProducts;
